import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  StepFrotaSchema,
  type StepFrotaFormValues,
} from '@/features/quotes/schemas/rd-equipamentos/step-frota.schema';
import { brlInputToCents } from '@/features/quotes/utils/rd-units.util';
import { rdEquipamentosService } from '@/services/quotes/rd-equipamentos';
import type {
  RdFleetPayload,
  RdProposal,
} from '@/services/quotes/rd-equipamentos/types';
import type { RdQuoteStepOnNextResult } from '@/features/quotes/components/rd-equipamentos/rd-quote-step.types';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { toaster } from '@/shared/ui/toaster.component';

type FieldMetaLike = { errorMap?: Record<string, unknown> };

/** Campos de nível superior com erro exibível — `fleetCoverages` é informativo e não exibe erro por linha. */
type SetFleetFormField = keyof Omit<StepFrotaFormValues, 'fleetCoverages'>;

/**
 * Superfície mínima de `useAppForm` (TanStack Form) usada por este hook —
 * mesma técnica de `use-add-rd-equipment.hook.ts`/`use-submit-rd-cliente.hook.ts`:
 * evita tipar os ~20 parâmetros genéricos de `FormApi`.
 */
/* eslint-disable no-unused-vars -- Numa ASSINATURA de tipo os nomes dos
   parâmetros não são variáveis: documentam o contrato de quem implementa
   (é o `setFieldMeta` do TanStack Form). A regra os lê como declarações
   não usadas; o desligamento vale só para este bloco de tipos. */
type SetFleetForm = {
  state: { values: StepFrotaFormValues };
  setFieldMeta: (
    field: SetFleetFormField,
    updater: (meta: FieldMetaLike) => FieldMetaLike
  ) => void;
};
/* eslint-enable no-unused-vars */

type UseSetRdFleetParams = {
  proposalId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ver nota em `form` acima (`SetFleetForm`)
  form: any;
  // eslint-disable-next-line no-unused-vars -- nome do parâmetro documenta o contrato da callback
  onNext: (result?: RdQuoteStepOnNextResult) => void;
  onBack: () => void;
};

const FORM_FIELDS: SetFleetFormField[] = ['riskValue', 'totalLmi', 'plano'];

function clearSubmitErrors(form: SetFleetForm): void {
  FORM_FIELDS.forEach(field => {
    form.setFieldMeta(field, meta => ({
      ...meta,
      errorMap: { ...meta.errorMap, onSubmit: undefined },
    }));
  });
}

function isSetFleetFormField(field: unknown): field is SetFleetFormField {
  return field === 'riskValue' || field === 'totalLmi' || field === 'plano';
}

/**
 * Normaliza os valores mascarados em moeda do formulário e valida via
 * `StepFrotaSchema` — compartilhado por `handleSubmit` (Próximo) e
 * `persistThenBack` (Voltar), que precisam do mesmo payload/validação mas
 * reagem diferente a uma falha de parse.
 */
function buildFleetPayload(values: StepFrotaFormValues) {
  const fleetCoverages = Object.fromEntries(
    Object.entries(values.fleetCoverages).map(([slug, display]) => [
      slug,
      brlInputToCents(display),
    ])
  );

  return StepFrotaSchema.safeParse({
    riskValue: brlInputToCents(values.riskValue),
    totalLmi: brlInputToCents(values.totalLmi),
    plano: values.plano,
    fleetCoverages,
    // `0` = ainda não escolhido; vira `undefined` para o schema aceitar e o
    // motor gravar NULL — quem exige o prazo é o cálculo, não o save.
    fleetIndemnityMonths: values.indemnityMonths || undefined,
    riskCep: values.riskCep || undefined,
    riskAddress: values.riskAddress || undefined,
    riskNumber: values.riskNumber || undefined,
    riskComplement: values.riskComplement || undefined,
    riskDistrict: values.riskDistrict || undefined,
    riskCity: values.riskCity || undefined,
    riskState: values.riskState || undefined,
  });
}

/**
 * Orquestra o submit do Step 2 agregado (Frota / LMI Único, RD Equipamentos):
 * normaliza os valores mascarados em moeda para centavos, valida via
 * `StepFrotaSchema`, chama `setFleet` e, no sucesso, refetcha
 * `['rd-proposal', proposalId]` (garante `riskValue`/`totalLmi`/`frotaPlano`
 * atualizados para os próximos steps) antes de avançar via `onNext`.
 */
export function useSetRdFleet({
  proposalId,
  form: formRaw,
  onNext,
  onBack,
}: UseSetRdFleetParams) {
  const form = formRaw as SetFleetForm;
  const queryClient = useQueryClient();
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const { mutateAsync, isPending } = useMutation<
    RdProposal,
    Error,
    RdFleetPayload
  >({
    mutationKey: ['quotes', 'rd-equipamentos', 'set-fleet', proposalId],
    mutationFn: async (payload: RdFleetPayload) => {
      const response = await rdEquipamentosService.setFleet(
        proposalId,
        payload
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao salvar a frota');
      }

      return response.data;
    },
  });

  async function handleSubmit() {
    setSubmitAttempted(true);
    clearSubmitErrors(form);

    if (!proposalId) {
      toaster.create({
        type: 'error',
        title: 'Dados incompletos',
        description:
          'Proposta não encontrada. Recarregue a página e tente novamente.',
      });
      return;
    }

    const parsed = buildFleetPayload(form.state.values);

    if (!parsed.success) {
      parsed.error.issues.forEach(issue => {
        const field = issue.path[0];
        if (!isSetFleetFormField(field)) return;
        form.setFieldMeta(field, meta => ({
          ...meta,
          errorMap: { ...meta.errorMap, onSubmit: issue.message },
        }));
      });
      return;
    }

    try {
      await mutateAsync(parsed.data);
      await queryClient.refetchQueries({
        queryKey: ['rd-proposal', proposalId],
      });
      setSubmitAttempted(false);
      onNext();
    } catch (error) {
      if (error instanceof ValidationError && error.fieldErrors) {
        Object.entries(error.fieldErrors).forEach(([field, fieldError]) => {
          if (!isSetFleetFormField(field)) return;
          form.setFieldMeta(field, meta => ({
            ...meta,
            errorMap: { ...meta.errorMap, onSubmit: fieldError.message },
          }));
        });
      }

      const description =
        error instanceof ValidationError
          ? error.detail?.trim() ||
            (error.messages.length ? error.messages.join('\n') : error.message)
          : error instanceof Error
            ? error.message
            : 'Erro inesperado';

      toaster.create({
        type: 'error',
        title: 'Não foi possível salvar a frota',
        description,
      });
    }
  }

  /**
   * Voltar (Step 2 agregado): salva os valores atuais da frota em segundo
   * plano, best-effort e NÃO bloqueante — sem exibir erros de validação
   * (`setFieldMeta`) nem toast de erro — antes de navegar para o step
   * anterior. Se os valores ainda não são válidos o suficiente para
   * persistir (o mesmo `StepFrotaSchema` do submit) ou o PUT falhar, apenas
   * segue para `onBack()` sem persistir (equivalente ao comportamento atual
   * para uma frota vazia/incompleta).
   */
  async function persistThenBack() {
    if (!proposalId) {
      onBack();
      return;
    }

    const parsed = buildFleetPayload(form.state.values);
    if (!parsed.success) {
      onBack();
      return;
    }

    try {
      await mutateAsync(parsed.data);
      await queryClient.refetchQueries({
        queryKey: ['rd-proposal', proposalId],
      });
    } catch {
      // Best-effort: falha ao salvar ao voltar não deve bloquear a navegação.
    } finally {
      onBack();
    }
  }

  return { handleSubmit, isPending, submitAttempted, persistThenBack };
}
